'use client';

import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { claimQuizAttempt } from '@/app/actions/quizzes';
import { readPendingQuizClaims, clearPendingQuizClaim } from '@/lib/quiz-claim';
import { callAction } from '@/lib/call-action';

/**
 * Drains pending anonymous quiz-attempt claims (see lib/quiz-claim) on the first
 * authenticated page load, linking each to the signed-in student via the backend
 * claim(). One attempt per quiz, so a replayer is claimed once, not once per play.
 * `claimQuizAttempt` is idempotent, so re-running is safe. Renders nothing.
 */
export function QuizAttemptClaimer({ isAuthenticated }: { isAuthenticated: boolean }) {
  const ranRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (ranRef.current) return;
    ranRef.current = true;

    const pending = readPendingQuizClaims();
    if (pending.length === 0) return;

    void (async () => {
      let claimed = 0;
      let failed = 0;
      for (const { quizId, attemptId } of pending) {
        const res = await callAction(() => claimQuizAttempt(attemptId));
        // Drop on success or on a permanent failure; keep on a transient error so
        // the next authenticated load retries.
        if (res.success) {
          claimed += 1;
          clearPendingQuizClaim(quizId);
        } else if (isPermanentClaimFailure(res.status)) {
          failed += 1;
          clearPendingQuizClaim(quizId);
        }
      }
      if (claimed || failed) posthog.capture('quiz_attempts_claimed', { claimed, failed });
    })();
  }, [isAuthenticated]);

  return null;
}

// 401 (no session), 403 (linked to another account) and 404 (attempt gone) never
// resolve on retry. Anything else — network blip, 5xx, unknown — is transient.
function isPermanentClaimFailure(status: number | undefined): boolean {
  return status === 401 || status === 403 || status === 404;
}
